// Cruzamento: pacientes/dispositivos do IW (FTP) × trocas registradas no Quality
// (trocas_dispositivos) × solicitações de troca hospitalar (dispositivos_solicitacoes).

// Dispositivos do IW (campos PB_*) que aparecem nos cards e na tabela.
export const DISPOSITIVOS_IW = [
  { id: 'gtt', nome: 'Gastrostomia', sigla: 'GTT' },
  { id: 'tqt', nome: 'Traqueostomia', sigla: 'TQT' },
  { id: 'o2', nome: 'Oxigênio', sigla: 'O₂' },
  { id: 'lpp', nome: 'Lesão por pressão', sigla: 'LPP' },
  { id: 'cvd', nome: 'Sonda vesical', sigla: 'SVD' },
  { id: 'picc', nome: 'PICC', sigla: 'PICC' },
  { id: 'colos', nome: 'Colostomia', sigla: 'COLO' },
  { id: 'avp', nome: 'Acesso venoso', sigla: 'AVP' },
  { id: 'port', nome: 'Port-a-cath', sigla: 'PORT' },
  { id: 'sne', nome: 'Sonda nasoenteral', sigla: 'SNE' },
];

// Periodicidade sugerida (dias) a partir dos registros existentes — Alexandre
// confirma depois. null = sem padrão (usa só a data registrada).
export const PERIODO_PADRAO = {
  'BOTTON': 180, 'SONDA KANGAROO': 180, 'SONDA PE': 180, 'TRAQUEOSTOMIA': 90,
  'SONDA FOLEY SILICONE': 30, 'SONDA FOLEY LATEX': 30, 'PICC': null, 'SNE': null,
};

// Qual dispositivo do IW cada tipo de troca corresponde.
export function familiaDaTroca(dispositivo) {
  const d = normalizar(dispositivo);
  if (/BOTTON|KANGAROO|SONDA PE|GASTRO|GTT/.test(d)) return 'gtt';
  if (/TRAQUEO|TQT|CANULA/.test(d)) return 'tqt';
  if (/FOLEY|VESICAL|SVD/.test(d)) return 'cvd';
  if (/PICC/.test(d)) return 'picc';
  if (/SNE|NASOENTERAL/.test(d)) return 'sne';
  return null;
}

export function normalizar(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();
}

function dataISO(s) {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

const DIA = 24 * 60 * 60 * 1000;
function hojeZero() { const h = new Date(); h.setHours(0, 0, 0, 0); return h; }

export function situacaoDoPrazo(dias) {
  if (dias === null) return { id: 'semData', rotulo: 'Sem data', cor: '#475569', fundo: '#f1f5f9' };
  if (dias < 0) return { id: 'vencida', rotulo: `Vencida há ${-dias} dia${dias === -1 ? '' : 's'}`, cor: '#991b1b', fundo: '#fee2e2' };
  if (dias === 0) return { id: 'ate7', rotulo: 'Vence hoje', cor: '#9a3412', fundo: '#ffedd5' };
  if (dias <= 7) return { id: 'ate7', rotulo: `Faltam ${dias} dia${dias === 1 ? '' : 's'}`, cor: '#9a3412', fundo: '#ffedd5' };
  if (dias <= 30) return { id: 'ate30', rotulo: `Faltam ${dias} dias`, cor: '#854d0e', fundo: '#fef9c3' };
  return { id: 'emDia', rotulo: `Faltam ${dias} dias`, cor: '#166534', fundo: '#dcfce7' };
}

const FINALIZADA = /realizad|cancelad|conclu/i;
const PARTICULAS = new Set(['DE', 'DA', 'DO', 'DAS', 'DOS', 'E']);

function tokens(nome) {
  return normalizar(nome).replace(/[^A-Z ]/g, ' ').split(' ').filter(t => t && !PARTICULAS.has(t));
}

function distancia(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let ant = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const at = [i];
    for (let j = 1; j <= n; j++) at[j] = Math.min(ant[j] + 1, at[j - 1] + 1, ant[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    ant = at;
  }
  return ant[n];
}

// Nomes "parecidos": mesmos nomes sem partículas, ou quase iguais (até ~8% de diferença),
// exigindo o mesmo primeiro e último nome para evitar confundir pacientes diferentes.
function parecido(a, b) {
  const ta = tokens(a), tb = tokens(b);
  if (!ta.length || !tb.length) return false;
  if (ta[0] !== tb[0] || ta[ta.length - 1] !== tb[tb.length - 1]) return false;
  const ja = ta.join(' '), jb = tb.join(' ');
  if (ja === jb) return true;
  return distancia(ja, jb) / Math.max(ja.length, jb.length) <= 0.08;
}

function dataBR(s) {
  const m = String(s || '').match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
}

/**
 * Casa um nome do Quality com o IW: paciente em atendimento (igual ou parecido)
 * ou, se não estiver em atendimento, o atendimento mais recente do histórico.
 */
function casarComIW(nome, ativosPorNome, ativos, historico, numIW, ativosPorNum) {
  // Registros vindos do FTP do IW já trazem o nº do atendimento: casa por ele, sem depender do nome
  if (numIW && ativosPorNum.has(String(numIW))) return { vinculo: 'ativo', pac: ativosPorNum.get(String(numIW)) };
  const n = normalizar(nome);
  if (ativosPorNome.has(n)) return { vinculo: 'ativo', pac: ativosPorNome.get(n) };
  const ativoParecido = ativos.find(p => parecido(nome, p.nome));
  if (ativoParecido) return { vinculo: 'ativoParecido', pac: ativoParecido };
  const doHist = historico.filter(h => normalizar(h.nome) === n || parecido(nome, h.nome));
  if (doHist.length) {
    const ultimo = doHist.sort((a, b) => (dataBR(b.entrada) || 0) - (dataBR(a.entrada) || 0))[0];
    return { vinculo: 'alta', hist: ultimo };
  }
  return { vinculo: 'naoEncontrado' };
}

/**
 * Monta as linhas de controle de troca (uma por paciente + tipo de dispositivo).
 * @returns {{ linhas: object[], semHistorico: object[] }}
 */
export function montarControleTrocas(pacientesIW, historicoIW, trocas, solicitacoes) {
  const ativosPorNome = new Map(pacientesIW.map(p => [normalizar(p.nome), p]));
  const ativosPorNum = new Map(pacientesIW.map(p => [String(p.numIW), p]));
  const historico = historicoIW || [];
  const hoje = hojeZero();

  // Último registro de troca de cada paciente + tipo de dispositivo.
  const ultimas = new Map();
  for (const t of trocas) {
    const chave = `${normalizar(t.paciente_nome)}|${normalizar(t.dispositivo)}`;
    const ref = dataISO(t.data_ultima_troca) || dataISO(t.registrado_em);
    const atual = ultimas.get(chave);
    if (!atual || (ref && (!atual._ref || ref > atual._ref))) ultimas.set(chave, { ...t, _ref: ref });
  }

  // Solicitações de troca ainda em andamento (troca hospitalar), por nº IW ou nome.
  const abertas = [];
  for (const s of solicitacoes) {
    if (FINALIZADA.test(s.status || '')) continue;
    abertas.push({ nome: s.paciente, familia: familiaDaTroca(s.dispositivo), status: s.status || 'em análise' });
  }

  const cacheCasamento = new Map();
  const casar = (nome, numIW) => {
    const n = `${normalizar(nome)}|${numIW || ''}`;
    if (!cacheCasamento.has(n)) cacheCasamento.set(n, casarComIW(nome, ativosPorNome, pacientesIW, historico, numIW, ativosPorNum));
    return cacheCasamento.get(n);
  };

  const linhas = [];
  const comHistorico = new Set();
  for (const t of ultimas.values()) {
    const fam = familiaDaTroca(t.dispositivo);
    const tipo = normalizar(t.dispositivo);
    const c = casar(t.paciente_nome, t.num_iw);
    const pac = c.pac || null;
    const ultima = dataISO(t.data_ultima_troca);
    let proxima = dataISO(t.data_proxima_troca);
    let origemProxima = 'registrada';
    const periodo = Number(t.periodo_dias) || PERIODO_PADRAO[tipo] || null;
    if ((!proxima || (ultima && proxima <= ultima)) && ultima && periodo) {
      proxima = new Date(ultima.getTime() + periodo * DIA);
      origemProxima = 'calculada';
    }
    const dias = proxima ? Math.round((proxima - hoje) / DIA) : null;
    const sol = abertas.find(a => a.familia === fam && (normalizar(a.nome) === normalizar(t.paciente_nome) || (pac && parecido(a.nome, pac.nome))));

    let alerta = null;
    if (c.vinculo === 'ativoParecido') alerta = `Nome no IW: ${pac.nome}`;
    if (pac && fam && pac.dispositivos[fam]?.valor === 'nao') alerta = 'IW diz que o paciente não usa mais esse dispositivo';
    if (c.vinculo === 'alta') alerta = `Alta no IW em ${c.hist.alta || '—'} (nº ${c.hist.numIW})`;
    if (c.vinculo === 'naoEncontrado') alerta = 'Não encontrado no IW (desde 2025)';
    if (pac && fam) comHistorico.add(`${pac.numIW}|${fam}`);

    const localDefinido = t.local_proxima_troca || '';
    linhas.push({
      id: t.id,
      chave: `${normalizar(t.paciente_nome)}|${tipo}`,
      nome: t.paciente_nome,
      numIW: pac?.numIW || c.hist?.numIW || '',
      unidade: pac?.unidade || c.hist?.unidade || '—',
      vinculo: c.vinculo,
      emAtendimento: !!pac,
      dispositivo: t.dispositivo,
      familia: fam,
      ultima, proxima, origemProxima, periodo, dias,
      situacao: situacaoDoPrazo(dias),
      localDefinido,
      solicitacao: sol ? sol.status : null,
      localDefinidoPor: t.local_definido_por || '',
      alerta,
      conferir: c.vinculo === 'ativoParecido' || c.vinculo === 'naoEncontrado' || (pac && fam && pac.dispositivos[fam]?.valor === 'nao'),
    });
  }

  // Pacientes que o IW diz usar GTT/TQT/SVD mas sem nenhuma troca registrada.
  const semHistorico = [];
  for (const p of pacientesIW) {
    for (const fam of ['gtt', 'tqt', 'cvd']) {
      if (p.dispositivos[fam]?.valor === 'sim' && !comHistorico.has(`${p.numIW}|${fam}`)) {
        semHistorico.push({ chave: `${p.numIW}|${fam}`, nome: p.nome, numIW: p.numIW, unidade: p.unidade, familia: fam });
      }
    }
  }
  return { linhas, semHistorico };
}

export function fmtData(d) {
  return d ? d.toLocaleDateString('pt-BR') : '—';
}
